import { cloneElement, isValidElement, type MouseEvent, type ReactElement, type ReactNode, useRef, useState } from 'react';

/**
 * Returns the open state of an overlay or panel, controlled when `open` is given.
 * :param open: the controlled state, or undefined for a component that keeps its own
 * :param onOpenChange: called with every change
 * :param defaultOpen: the first state of a component that keeps its own
 * :return: the state and a setter
 */
export function useOpenState(
  open: boolean | undefined,
  onOpenChange?: (open: boolean) => void,
  defaultOpen = false,
): [boolean, (open: boolean) => void] {
  const [uncontrolled, setUncontrolled] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    if (open === undefined) setUncontrolled(next);
    onOpenChange?.(next);
  };
  return [open ?? uncontrolled, setOpen];
}

type TriggerProps = {
  'aria-haspopup'?: string;
  'aria-expanded'?: boolean;
  onClick?: (event: MouseEvent<HTMLElement>) => void;
};

/**
 * Returns the wrapper ref, the anchor and the trigger of an overlay. A click on the trigger
 * calls `onToggle`, and the trigger reports the popup it opens and whether it is open.
 * :param trigger: the element that opens the overlay
 * :param open: whether the overlay is open
 * :param popup: the popup kind, used when the trigger names none itself
 * :param onToggle: called on each click on the trigger
 * :return: the ref for the wrapper, the anchor for MUI, and the trigger to show
 */
export function useTrigger(trigger: ReactNode, open: boolean, popup: 'menu' | 'dialog', onToggle: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  const anchorEl = () => ref.current?.firstElementChild ?? ref.current ?? document.body;
  const element = isValidElement<TriggerProps>(trigger)
    ? cloneElement(trigger as ReactElement<TriggerProps>, {
        'aria-haspopup': trigger.props['aria-haspopup'] ?? popup,
        'aria-expanded': open,
        onClick: (event: MouseEvent<HTMLElement>) => {
          trigger.props.onClick?.(event);
          onToggle();
        },
      })
    : trigger;
  return { ref, anchorEl, element };
}

export interface AnchorPoint {
  x: number;
  y: number;
}

/**
 * Returns the MUI anchor props of a popover or menu.
 * :param anchorEl: the element under which the overlay opens
 * :param anchorPoint: a viewport point to open at in place of the element
 * :param align: the edge of the anchor the overlay lines up with
 * :return: the anchor, position and origin props
 */
export function anchorProps(anchorEl: () => Element, anchorPoint: AnchorPoint | null, align: 'start' | 'end') {
  const horizontal = align === 'end' ? 'right' : 'left';
  return {
    anchorEl,
    anchorReference: anchorPoint ? ('anchorPosition' as const) : ('anchorEl' as const),
    anchorPosition: anchorPoint ? { top: anchorPoint.y, left: anchorPoint.x } : undefined,
    anchorOrigin: { vertical: 'bottom' as const, horizontal },
    transformOrigin: { vertical: 'top' as const, horizontal },
  } as const;
}
